import PropTypes from "prop-types";
import {
  TextField as MuiTextField,
  Autocomplete as MuiAutocomplete,
} from "@mui/material";
import { ErrorMessage } from "formik";
import { useTranslation } from "react-i18next";

/**
 * @callback AutocompleteOnChange
 * @param {any} val The selected value for the field
 * @param {Function} setValue Formik's value setter for this Field
 */

/**
 * A custom component for use with Formik's Field component that also wraps
 * Material UI's TextField component so we can take advantage of both tools.
 *
 * @param {object} props
 * @param {object} props.field Formik's field value object -- { name, value, onChange, onBlur }
 * @param {object} props.form Formik's form object
 * @param {string} props.label A react-i18next translation file entry
 * @param {any[]} props.options The list of options for the autocomplete's dropdown
 * @param {AutocompleteOnChange} [props.onChange] Optional onChange callback to be executed
 *        This custom onChange will be passed the field setter & is expected to manage the field state.
 * @param {string} [props.fieldSize] Size value to be passed to MUI's TextField.size prop inside the Autocomplete.renderInput prop (Default: medium)
 * @param {object} [props.inputProps] Any additional attributes to pass via MUI's TextField.inputProps prop inside the Autocomplete.renderInput prop
 * @param {object} [props.styles] Any additional styles, passed to MUI's sx prop
 *
 * @see https://formik.org/docs/api/field#component
 * @see https://mui.com/material-ui/api/autocomplete
 */
const FormikAutocomplete = ({
  field,
  form,
  label,
  options,
  onChange = null,
  fieldSize = "medium",
  inputProps = {},
  styles = {},
  ...otherProps
}) => {
  const { t } = useTranslation();
  const { touched, errors } = form;
  const { name, value, onChange: formikOnChange } = field;
  const hasError = errors[name] && touched[name];
  const changeHandler = onChange ?? formikOnChange;
  const customOnChange = !!onChange;

  // Ensure freeSolo Autocompletes are able to update Formik state
  //  (TODO: see if this is necessary, why not a ternary in the onInputChange prop directly?)
  const onInputChangeDefault = otherProps.onInputChange ?? (() => {});
  const onInputChange = !otherProps.freeSolo
    ? otherProps.onInputChange
    : (e, value, reason) => {
        form.setFieldValue(name, value);
        onInputChangeDefault(e, value, reason);
      };

  return (
    <MuiAutocomplete
      id={`formik-mui-autocomplete-${name}`}
      name={name}
      options={options}
      value={value}
      onChange={(e, val) => {
        if (customOnChange) {
          // Get the field setter to inject into the custom onChange
          const { setValue } = form.getFieldHelpers(field.name);
          changeHandler(val, setValue);
        } else {
          form.setFieldValue(name, val);
        }
      }}
      onInputChange={onInputChange}
      onBlur={() => form.setFieldTouched(name, true)}
      forcePopupIcon
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label={t(label)}
          variant="outlined"
          required={otherProps.required}
          size={fieldSize}
          error={hasError}
          helperText={hasError ? <ErrorMessage name={name} /> : null}
          slotProps={{
            input: { ...params.InputProps },
            htmlInput: { ...params.inputProps, ...inputProps },
          }}
        />
      )}
      renderOption={(optProps, option) => (
        <div style={{ fontSize: "14px" }} {...optProps} key={optProps.key}>
          {option}
        </div>
      )}
      // Spread otherProps last to override any of the above
      {...otherProps}
      // When the field is disabled & the value reset, FreeSolo causes values
      // to linger, make it dependent on the enable state so when MUI
      // reverts to a normal Autocomplete the value is cleared like
      // non-freeSolo ones.
      freeSolo={otherProps.freeSolo && !otherProps.disabled}
      sx={{
        "& .MuiAutocomplete-endAdornment .MuiIconButton-root": {
          // Move dropdown arrow and clear X up to horizontal center
          height: "auto",
        },
        "& .MuiAutocomplete-popupIndicatorOpen": {
          // Stop MUI from rotating the icon by rotating it back
          rotate: "180deg",
        },
        "& .MuiInputLabel-root, .MuiInputBase-input": {
          fontSize: "14.5px",
        },
        ...styles,
      }}
    />
  );
};

FormikAutocomplete.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  fieldSize: PropTypes.string,
  inputProps: PropTypes.object,
  styles: PropTypes.object,
};

export default FormikAutocomplete;
